import { ModelCatalog, type PublishModelTier, type SetManagedModelOffer } from "@cleo/contracts";
import * as Schema from "effect/Schema";
import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
import { useNeucleosWorldStore } from "./worldStore";

const pendingCatalogReads = new Map<string, Promise<ModelCatalog>>();

export async function readModelCatalog(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ModelCatalog> {
  if (fetchImpl !== fetch)
    return modelCatalogRequest("model-catalog/read", { worldKey }, worldKey, fetchImpl);
  const grantRevision = useNeucleosWorldStore.getState().grantRevision;
  const key = `${grantRevision}:${worldKey}`;
  const pending = pendingCatalogReads.get(key);
  if (pending) return pending;
  const request = modelCatalogRequest("model-catalog/read", { worldKey }, worldKey, fetchImpl)
    .then((value) => {
      if (useNeucleosWorldStore.getState().grantRevision !== grantRevision)
        throw new Error("World access changed while reading models. Read them again.");
      return value;
    })
    .finally(() => {
      if (pendingCatalogReads.get(key) === request) pendingCatalogReads.delete(key);
    });
  pendingCatalogReads.set(key, request);
  return request;
}

export async function publishModelTier(
  worldKey: string,
  input: PublishModelTier,
  fetchImpl: typeof fetch = fetch,
): Promise<ModelCatalog> {
  return modelCatalogRequest("model-tiers/publish", { worldKey, ...input }, worldKey, fetchImpl);
}

export async function setManagedModelOffer(
  worldKey: string,
  input: SetManagedModelOffer,
  fetchImpl: typeof fetch = fetch,
): Promise<ModelCatalog> {
  return modelCatalogRequest("model-catalog/offer", { worldKey, ...input }, worldKey, fetchImpl);
}

async function modelCatalogRequest(
  path: string,
  payload: unknown,
  worldKey: string,
  fetchImpl: typeof fetch,
): Promise<ModelCatalog> {
  const response = await postLocalJson(`/api/neucleos/world/${path}`, payload, fetchImpl);
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.ok !== true)
    throw new Error(
      typeof body?.reason === "string" ? body.reason : "Models could not be read. Try refreshing.",
    );
  if (!Schema.is(ModelCatalog)(body.value) || body.value.worldKey !== worldKey)
    throw new Error("The World returned an invalid model catalog.");
  return body.value;
}
